import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'
import { unlock } from '../audio/bus.js'
import { notePath } from '../pwa/busy.js'
import { useDailyStore } from '../state/daily.js'
import { useChildStore } from '../state/store.js'
import { Toast } from '../ui/index.js'
import { useDevSeed } from './devSeed.js'

/**
 * One layout for every screen: a safe area padded column, the toast host, and the two document
 * level switches the design system reads, `data-mode` (Mini or Core sizing) and `data-area`
 * (the calmer parent palette).
 */
export function AppShell() {
  const location = useLocation()
  const mode = useChildStore((store) => store.state?.profile.mode ?? 'core')

  // Dev only, and only with `?seed=`: gives the screenshot gate a child to look at.
  useDevSeed()

  // Günün İçeriği (W3-OVERHAUL §2.6): one request per local date, on open and at midnight.
  const loadDaily = useDailyStore((store) => store.load)
  useEffect(() => {
    void loadDaily()
    const timer = window.setInterval(() => void loadDaily(), 15 * 60 * 1000)
    return () => window.clearInterval(timer)
  }, [loadDaily])

  useEffect(() => {
    document.documentElement.dataset.mode = mode
  }, [mode])

  useEffect(() => {
    document.documentElement.dataset.area = location.pathname.startsWith('/veli')
      ? 'parent'
      : 'child'
  }, [location.pathname])

  // A waiting service worker update reloads the page at a route change and never inside a
  // ritual, a lesson or a game (app/src/pwa/busy.ts).
  useEffect(() => {
    notePath(location.pathname)
  }, [location.pathname])

  // CONVENTIONS §8: the AudioContext may only start from a real gesture. The first tap
  // anywhere in the shell is that gesture.
  useEffect(() => {
    const onFirstTap = () => {
      void unlock()
    }
    document.addEventListener('pointerdown', onFirstTap, { once: true })
    return () => document.removeEventListener('pointerdown', onFirstTap)
  }, [])

  return (
    <div
      style={{
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        padding:
          'calc(var(--space-4) + env(safe-area-inset-top)) var(--space-4) calc(var(--space-5) + env(safe-area-inset-bottom))',
        maxWidth: 720,
        margin: '0 auto',
        width: '100%',
      }}
    >
      {/*
        QA-026: the short screens (welcome, quiz, brush result) were top anchored, so on a
        phone the bottom half of the page was empty paper under the one thing the child had to
        look at. `justify-content: center` only moves a screen that is shorter than the
        viewport; a tall one (Yuva, the map) grows and is untouched.
      */}
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Outlet />
      </main>
      <Toast />
    </div>
  )
}
